import { Box, Typography } from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import { observer } from 'mobx-react';
import React from 'react';
import { useStores } from 'stores/storeInitializer';
import { CompanyCardData } from './companyCardData';

interface CompanyCardProps {
    data: CompanyCardData;
}

const wrap = { overflowWrap: 'anywhere', wordBreak: 'break-word' } as const;

/**
 * The company BOLT found, for the customer to check. Read-only on purpose: plain text in a description list, no
 * input of any kind, so nothing here can be edited or submitted.
 */
export const CompanyCard: React.FC<CompanyCardProps> = observer(({ data }) => {
    const { store } = useStores();
    const headingId = React.useId();

    const rows: Array<{ label: string; lines: string[] }> = [
        { label: store.L(LocalizationKeys.BillingDetailsCompanyName), lines: [data.name] },
        { label: store.L(LocalizationKeys.BillingDetailsAddress), lines: data.addressLines },
        ...(data.invoiceIdentifier
            ? [{ label: store.L(LocalizationKeys.BillingDetailsInvoiceIdentifier), lines: [data.invoiceIdentifier] }]
            : []),
    ];

    return (
        <Box
            component="section"
            aria-labelledby={headingId}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.paper' }}
        >
            <Typography id={headingId} variant="subtitle1" component="h2" fontWeight={700} gutterBottom>
                {store.L(LocalizationKeys.BillingDetailsCompanyDetails)}
            </Typography>
            <Box component="dl" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {rows.map(({ label, lines }) => (
                    <Box key={label}>
                        <Typography component="dt" variant="caption" color="text.secondary">
                            {label}
                        </Typography>
                        <Typography component="dd" variant="body1" sx={{ m: 0, ...wrap }}>
                            {lines.map((line, index) => (
                                <React.Fragment key={`${index}-${line}`}>
                                    {index > 0 ? <br /> : null}
                                    {line}
                                </React.Fragment>
                            ))}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
});

export default CompanyCard;
